import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';
import { authFetch } from '@/utils/auth-fetch';
import Home from './home';

/**
 * task-n1e23erl53 (CEO 09-12) — the admin Home: the latest news about the imports the user has access to, and the
 * products skipped or blocked, grouped by REASON, with the category-research gap first-class. Read-only; the only
 * action is the link to the research screen. The reason list is the backend census (services/importNewsReasons.js),
 * never a hand-picked list here. Design: docs/delivery/stories/task-n1e23erl53/tech/design.md.
 */

type StepRow = { step: string; status: string | null; metrics: Record<string, unknown> | null };
type ImportRun = {
  id: string; status: string | null; trigger_type: string | null; started_at: string | null; completed_at: string | null;
  created_at: string; error_message: string | null; processing_mode: string | null;
  summary: Record<string, unknown> | null; total_sub_batches: number | null; completed_sub_batches: number | null; failed_sub_batches: number | null;
  steps: StepRow[]; pages: Record<string, number>;
};
type ReasonLine = {
  kind: string; key: string; label: string; source: string; products: number | null; rows: number; newest: string | null;
  research_gap: boolean; unlabelled?: boolean;
  detail?: { sector_id?: number | null; category_path?: string | null; phase?: string | null; is_terminal?: boolean | null; feed_item_count?: number | null; bucket?: string | null; sector_wide?: boolean; literal?: string; category_pages?: number | null } | null;
};
type SitePanel = {
  site_id: string; name: string;
  imports: ImportRun[];
  blocked: ReasonLine[];
  research_gap: { status: 'published' | 'not_published'; source_task?: string; computed_at?: string | null; lines: ReasonLine[]; note?: string | null };
  freshness: Record<string, string | null>;
};
type ImportNews = { computed_at: string; days: number; sites: SitePanel[] };

const fmt = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—');
const num = (v: unknown) => (typeof v === 'number' ? v : v == null ? '—' : String(v));
const stepMetric = (run: ImportRun, step: string, key: string): unknown => {
  const s = run.steps?.find((x) => x.step === step);
  return s?.metrics ? (s.metrics as Record<string, unknown>)[key] : undefined;
};

// PR #6089 round 5 (codex-reviewer-2, minor): the ORIGINATING site rides on the link. In "All my sites" a category of site B
// is listed while site A is selected; the destination's catalogue read is site-scoped, so without this it reads A's
// catalogue and reports B's row missing.
export const researchLink = (sectorId: number | null | undefined, categoryPath: string | null | undefined, siteId?: string | null) => {
  const q = new URLSearchParams();
  if (sectorId != null) q.set('sectorId', String(sectorId));
  if (categoryPath) q.set('categoryPath', categoryPath);
  if (siteId) q.set('siteId', siteId);
  return `/admin/category-research?${q.toString()}`;
};

const ImportNewsHome: React.FC = () => {
  const { selectedSite, sites } = useSite();
  const { user } = useAuth();
  const [allMySites, setAllMySites] = useState(false);
  const [days, setDays] = useState(30);
  const [data, setData] = useState<ImportNews | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const siteIds = useMemo(() => {
    if (allMySites) return (sites || []).map((s) => s.id || s._id).filter(Boolean) as string[];
    const id = selectedSite?.id || selectedSite?._id;
    return id ? [id] : [];
  }, [allMySites, selectedSite, sites]);

  // The effect keys on the JOINED ids, not the array: a site context that hands back a fresh array each render would
  // otherwise re-run the read forever and cancel every result before it lands.
  const siteKey = siteIds.join(',');
  useEffect(() => {
    let cancelled = false;
    if (siteKey === '') { setData(null); setLoading(false); return; }
    // PR #6089 round 7 (codex-reviewer, minor): the previous answer is CLEARED when the scope (sites or window) changes.
    // Kept, it sat on screen as if it were the new scope's answer — 30-day rows under a "Last 7 days" selection — and
    // stayed there indefinitely beside the error when the replacement request failed.
    setData(null); setLoading(true); setError(null);
    authFetch(`/api/home/import-news?siteIds=${encodeURIComponent(siteKey)}&days=${days}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`import-news ${res.status}: ${(await res.text()).slice(0, 200)}`);
        return res.json();
      })
      .then((json) => { if (!cancelled) setData(json); })
      .catch((e) => { if (!cancelled) setError(e.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [siteKey, days]);

  return (
    <TooltipProvider>
      <div className="container mx-auto p-6 space-y-6" data-testid="import-news-home">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">Knowledge graph news</h1>
            <p className="text-muted-foreground">The latest imports you have access to, and the products skipped or blocked, by reason.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant={allMySites ? 'default' : 'outline'} size="sm" onClick={() => setAllMySites((v) => !v)} data-testid="toggle-all-sites">
              {allMySites ? 'All my sites' : 'Selected site'}
            </Button>
            <select className="border rounded px-2 py-1 text-sm" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Window in days" data-testid="days-select">
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>

        {user?.role === 'site_content_manager' && (
          <p className="text-sm"><Link className="underline" to="/dashboard/content-manager">Open the content manager workspace</Link></p>
        )}
        {siteIds.length === 0 && <p className="text-muted-foreground" data-testid="no-site">Select a site to see its news.</p>}
        {loading && siteIds.length > 0 && <p className="text-muted-foreground">Loading…</p>}
        {error && <p className="text-destructive" role="alert">{error}</p>}

        {data?.sites.map((site) => (
          <section key={site.site_id} className="space-y-4" data-testid={`site-${site.site_id}`}>
            <h2 className="text-2xl font-semibold">{site.name}</h2>

            <Card>
              <CardHeader>
                <CardTitle>Latest imports</CardTitle>
                <CardDescription>Newest {site.imports.length} runs · computed {fmt(data.computed_at)} · newest run {fmt(site.freshness.imports)}</CardDescription>
              </CardHeader>
              <CardContent>
                {site.imports.length === 0 ? <p className="text-muted-foreground">No import runs in the last {data.days} days.</p> : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Started</TableHead><TableHead>Status</TableHead><TableHead>Products</TableHead><TableHead>New / updated</TableHead>
                      <TableHead>Research</TableHead><TableHead>Enrichment queued</TableHead><TableHead>AEO imported / skipped</TableHead><TableHead>Pages</TableHead><TableHead>Sub-batches</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {site.imports.map((run) => {
                        const researchSkipped = stepMetric(run, 'research', 'skipped');
                        const researchReason = stepMetric(run, 'research', 'reason');
                        return (
                          <TableRow key={run.id} data-testid={`run-${run.id}`}>
                            <TableCell>{fmt(run.started_at || run.created_at)}</TableCell>
                            <TableCell><Badge variant={run.status === 'completed' ? 'default' : run.status === 'failed' ? 'destructive' : 'secondary'}>{run.status || '—'}</Badge>{run.error_message ? <div className="text-xs text-destructive mt-1">{run.error_message.slice(0, 160)}</div> : null}</TableCell>
                            <TableCell>{num(run.summary?.total_products)}</TableCell>
                            <TableCell>{num(run.summary?.new_products)} / {num(run.summary?.updated_products)}</TableCell>
                            <TableCell>{researchSkipped === true || researchSkipped === 'true' ? <Badge variant="destructive">skipped: {String(researchReason || 'no reason')}</Badge> : <span>{num(stepMetric(run, 'research', 'researches_successful'))} ok</span>}</TableCell>
                            <TableCell>{num(stepMetric(run, 'enrichment', 'products_queued'))}</TableCell>
                            <TableCell>{num(stepMetric(run, 'aeo', 'kb_imported'))} / {num(stepMetric(run, 'aeo', 'kb_skipped'))}</TableCell>
                            <TableCell>{Object.entries(run.pages || {}).map(([st, n]) => `${st}: ${n}`).join(', ') || '—'}</TableCell>
                            <TableCell>{num(run.completed_sub_batches)} / {num(run.total_sub_batches)}{run.failed_sub_batches ? <span className="text-destructive"> ({run.failed_sub_batches} failed)</span> : null}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card data-testid="research-gap-card">
              <CardHeader>
                <CardTitle>Categories lacking research</CardTitle>
                <CardDescription>
                  {site.research_gap.status === 'published'
                    ? `(sector, category) pairs the taxonomy preflight found without a researched leaf row with personas — the sector-scoped persona gate. Counts are SECTOR-WIDE (every site on the sector). List read ${fmt(site.research_gap.computed_at)}; a stale read means the preflight was not re-run, not that the gap closed.`
                    : `Preflight list not published yet (${site.research_gap.source_task || 'task-3qxxj4jxeb'}) — blocked products already recorded appear below.`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {site.research_gap.status !== 'published' ? (
                  <p className="text-muted-foreground" data-testid="research-gap-unavailable">Count unavailable: the preflight list is not published yet, so this cannot say whether anything is pending.</p>
                ) : site.research_gap.note ? (
                  // PR #6089 round 8 (both legs, minor): the service returns status published, no lines and a NOTE when it did not
                  // query at all (a site with no sector). A note the service writes and the page throws away is the silent-fallback
                  // shape: it rendered "Nothing pending" for a site that was never assessed.
                  <p className="text-muted-foreground" data-testid="research-gap-unassessable">Cannot assess research gaps: {site.research_gap.note}. The preflight list is read by sector, and this site has none assigned.</p>
                ) : site.research_gap.lines.length === 0 ? <p className="text-muted-foreground" data-testid="research-gap-empty">Nothing pending.</p> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Category</TableHead><TableHead>Sector</TableHead><TableHead>Products</TableHead><TableHead>Newest</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {site.research_gap.lines.map((l, i) => (
                        <TableRow key={`${l.key}-${i}`}>
                          <TableCell>{l.detail?.category_path || '—'}{l.detail?.bucket ? <span className="text-xs text-muted-foreground"> · bucket {l.detail.bucket}</span> : null}</TableCell>
                          <TableCell>{l.detail?.sector_id ?? '—'}</TableCell>
                          <TableCell data-testid="gap-count">
                            <Tooltip>
                              <TooltipTrigger asChild><span>{num(l.products)}{l.detail?.sector_wide ? <Badge variant="outline" className="ml-2">sector-wide</Badge> : null}</span></TooltipTrigger>
                              <TooltipContent>
                                {l.detail?.sector_wide ? 'SECTOR-WIDE count (every site on this sector, not this site alone) · ' : 'this site · '}
                                canonical products: {num(l.products)} · feed items incl. variants: {l.detail?.feed_item_count != null ? l.detail.feed_item_count : 'not published'}
                              </TooltipContent>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{fmt(l.newest)}</TableCell>
                          <TableCell><Link className="underline" to={researchLink(l.detail?.sector_id, l.detail?.category_path, site.site_id)} data-testid="research-link">Research this category</Link></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card data-testid="blocked-card">
              <CardHeader>
                <CardTitle>Products skipped or blocked, by reason</CardTitle>
                <CardDescription>Last {data.days} days · computed {fmt(data.computed_at)} · newest event {fmt(site.freshness.blocked)}</CardDescription>
              </CardHeader>
              <CardContent>
                {site.blocked.length === 0 ? <p className="text-muted-foreground">Nothing skipped or blocked in the window.</p> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Reason</TableHead><TableHead>Products</TableHead><TableHead>Newest</TableHead><TableHead>Where</TableHead><TableHead>Source</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {site.blocked.map((l, i) => (
                        <TableRow key={`${l.kind}-${l.key}-${i}`} data-testid={`reason-${l.kind}-${l.key}`}>
                          <TableCell>{l.research_gap ? <Badge variant="destructive">research</Badge> : null}{l.unlabelled ? <Badge variant="outline">unlabelled</Badge> : null} {l.label}</TableCell>
                          <TableCell data-testid="reason-count">
                            {l.products != null ? num(l.products) : <span title="no product count for this line">—</span>}
                            {l.kind === 'translation' ? <span className="text-xs text-muted-foreground"> · {l.rows} translation row{l.rows === 1 ? '' : 's'}{l.detail?.category_pages != null ? ` · ${l.detail.category_pages} category page${l.detail.category_pages === 1 ? '' : 's'}` : ''}</span> : null}
                          </TableCell>
                          <TableCell>{fmt(l.newest)}</TableCell>
                          <TableCell>
                            {l.detail?.category_path ? <span>{l.detail.category_path}{l.detail.sector_id != null ? ` (sector ${l.detail.sector_id})` : ''} {l.research_gap ? <Link className="underline ml-2" to={researchLink(l.detail.sector_id, l.detail.category_path, site.site_id)} data-testid="research-link">Research</Link> : null}</span> : '—'}
                            {l.detail?.phase ? <span className="text-xs text-muted-foreground"> · {l.detail.phase}{l.detail.is_terminal === false ? ' (non-terminal)' : ''}</span> : null}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{l.source}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </section>
        ))}

        <Collapsible>
          <CollapsibleTrigger asChild><Button variant="ghost" data-testid="quick-actions-toggle">Quick actions &amp; setup</Button></CollapsibleTrigger>
          <CollapsibleContent><Home /></CollapsibleContent>
        </Collapsible>
      </div>
    </TooltipProvider>
  );
};

export default ImportNewsHome;
